import type { SwitchingEuropeProps } from '@/components/home/SwitchingEurope';
import { BottomCTAProps } from '@/components/homeowners/BottomCTA';
import type { CoverageProps } from '@/components/homeowners/coverage/Coverage';
import type { FaqProps } from '@/components/homeowners/faq/Faq';
import { faqInfo } from '@/components/homeowners/faq/FaqDataFrEn';
import type { HeroProps } from '@/components/homeowners/Hero';
import type { CustomersReviewsProps, ProductsProps } from '@/components/shared';
import { PRODUCT_HOME_EU_HOMEOWNERS } from '@/components/shared/BuildByYou/defaults';
import { sharedConfig } from '@/pageConfig/shared';
import damageImgLg from '@/static/assets/homeowners/coverage/fr/lg/damaged.svg';
import theftImgLg from '@/static/assets/homeowners/coverage/fr/lg/emergency.svg';
import leaksImgLg from '@/static/assets/homeowners/coverage/fr/lg/leaks.svg';
import lawImgLg from '@/static/assets/homeowners/coverage/fr/lg/legal.svg';
import fallenTreeImgLg from '@/static/assets/homeowners/coverage/fr/lg/property_liability.svg';
import unlivableImgLg from '@/static/assets/homeowners/coverage/fr/lg/unlivable.svg';
import damageImgSm from '@/static/assets/homeowners/coverage/fr/sm/damaged.svg';
import theftImgSm from '@/static/assets/homeowners/coverage/fr/sm/emergency.svg';
import leaksImgSm from '@/static/assets/homeowners/coverage/fr/sm/leaks.svg';
import lawImgSm from '@/static/assets/homeowners/coverage/fr/sm/legal.svg';
import fallenTreeImgSm from '@/static/assets/homeowners/coverage/fr/sm/property_liability.svg';
import unlivableImgSm from '@/static/assets/homeowners/coverage/fr/sm/unlivable.svg';
import houseLg from '@/static/assets/homeowners/houses/hero_fr/desktop.svg';
import houseSm from '@/static/assets/homeowners/houses/hero_fr/mobile.svg';
import ogImage from '@/static/assets/homeowners/og-fr-en.png';
import windowImage from '@/static/assets/homeowners/window_FR.svg';
import chatEnDesktopLottieConfig from '@/static/genericProducts/chatEnDesktopLottie.json';
import chatEnLottieConfig from '@/static/genericProducts/chatEnLottie.json';
import mapDesktopLottieConfig from '@/static/genericProducts/mapDesktopLottie.json';
import mapLottieConfig from '@/static/genericProducts/mapLottie.json';
import successDesktopLottieConfig from '@/static/genericProducts/successDesktopLottie.json';
import successLottieConfig from '@/static/genericProducts/successLottie.json';
import type { BasePageConfig } from '../../BasePageConfig';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade France Homeowners Insurance',
  description: 'Insurance policies for homeowners in France',
  image: [
    'https://www.lemonade.com/assets/home/homeowners/right_homeowners_mobile-af50b03b1ecfb81df02efe6a2a19bf7e4e07b565751c22483da27f612caeadb1.svg',
    'https://www.lemonade.com/assets/home/homeowners/right_homeowners-af94926a22ed9f66fbabc8d23a6f705b1c22ea0de0b82e0de3089f081bf8b13a.svg',
  ],
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '10',
    highPrice: '',
    priceCurrency: 'EUR',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does homeowners insurance cover?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our homeowners insurance includes three main components: Coverage for the structure of the home you own (which includes its walls, flooring, roof, etc.), coverage for the items that you own and normally keep inside of your home, and liability coverage if you accidentally cause damage or injury to someone else.',
      },
    },
    {
      '@type': 'Question',
      name: 'What kind of unfortunate things am I covered for?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We will cover the damage to your home and your stuff due to fire, smoke, explosion, power surge, windstorms, hail, snow pressure, collisions by vehicles, and water damage. For more details, create a free quote or check out our policy.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does homeowners insurance cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Homeowners insurance at Lemonade starts at 10 €/month, but varies depending  on factors including your house’s age, the cost to rebuild, building materials, as well as the coverage and add-ons you choose.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is your repair network?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If, after an incident, your home needs to be fixed up, Lemonade will provide you access to a Repair Network with a wide range of providers in France that can help repair your building parts.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I receive my home insurance certificate?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You’ll receive your Lemonade home insurance certificate by email just after getting your policy, but in case of doubt and to always have it in your pocket, you can also download it again from the app!',
      },
    },
    {
      '@type': 'Question',
      name: 'What additional coverages can I add to my policy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Among the additional coverages you can add are burglary and vandalism, complete theft coverage, glass breakage, school insurance, and legal protection.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are my outbuildings covered?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'When you make a quote with Lemonade, our chatbot Maya will ask you for information about your home. If  you share information about  your garage, garden shed, storage unit, swimming pool, fireplace, solar panel, and veranda during the quote flow, then we’ve got you covered!',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I file a claim?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Just open the Lemonade app and hit the ‘Claim’ button! We’ll guide you through the process from there. Once your claim is approved, we’ll deposit your payment, minus your deductible, directly into your account.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I reach you?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can get in touch with us through the app or website. You can also call us at +33 801 840 505 or reach out via email at <a href="mailto:contact@lemonade.com">contact@lemonade.com</a>. In case of emergency only and if you wish to contact our assistance services, please call our assistance partners at: <a href="tel:+33801840506">+33 801 840506</a>.',
      },
    },
  ],
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  products: ProductsProps;
  coverage: CoverageProps;
  bundleSection?: unknown;
  faq: FaqProps;
  bottomCTA: BottomCTAProps;
  switchingEurope: SwitchingEuropeProps;
}

export const config: Config = {
  layout: {
    product: PRODUCT_HOME_EU_HOMEOWNERS,
    ...sharedConfig['en-FR'].layout,
    headerActions: {
      ...sharedConfig['en-FR'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-FR'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-FR'].layout.headerActions.CTAs.checkOurPrices,
          customUrl: '/onboarding/start?f=1&ownership=owner',
        },
      },
    },
  },
  seo: {
    title: 'Homeowners insurance by Lemonade - Get a free quote in minutes',
    metaTitle: 'Homeowners insurance by Lemonade - Get a free quote in minutes',
    description:
      "Get homeowners insurance that you'll actually love. Zero hassle. Great prices. Incredibly fast. Starting from 10 € /month.",
    ogImage: ogImage.src,
    jsonld: [productJsonld, faqSchema],
    uniqueCanonicalUrl: 'https://www.lemonade.com/fr/en/home-insurance',
  },
  hero: {
    title: 'Homeowners insurance\nthat you’ll actually love',
    subtitle: 'Zero hassle. Incredibly fast. Great prices.',
    ctaUrl: '/onboarding/start?f=1&ownership=owner',
    frontHouseImage: {
      lg: houseLg.src,
      sm: houseSm.src,
      houseTranslateY: {
        lg: -10,
        sm: -10,
      },
    },
  },
  customersReviews: {
    title: 'Over 2,000,000 happy customers!',
    subtitle:
      'Lemonade has earned 4.9 stars in the App store, and is\ntop-rated on <a href="https://fr.trustpilot.com/review/lemonade.com" target="_blank">Truspilot</a>',
    darkBackground: true,
    showFullStars: true,
  },
  coverage: {
    title: 'What does homeowners insurance cover?',
    subtitle: 'High quality home insurance for your home and your stuff',
    data: [
      {
        title: 'If your home\nis damaged',
        description: 'We’ll cover the cost of rebuilding your home.',
        img: {
          src: {
            lg: damageImgLg,
            sm: damageImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'Protection against\nproperty liability',
        description:
          'You’ll be covered if an accident in your home (like a fire, water, leak of explosion) causes damage to your neighbour’s home.',
        img: {
          src: {
            lg: fallenTreeImgLg,
            sm: fallenTreeImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'If your home\nis unlivable',
        description: 'We’ll cover expenses until you get back on your feet.',
        img: {
          src: {
            lg: unlivableImgLg,
            sm: unlivableImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'If there is\na water leak',
        description: 'We’ll pay for the search and repair for leaks coming from your roof, facade or bathroom seals.',
        img: {
          src: {
            lg: leaksImgLg,
            sm: leaksImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'If you need\nemergency assistance',
        description: 'We’ll provide 24/7 assistance in case of an emergency in your home.',
        img: {
          src: {
            lg: theftImgLg,
            sm: theftImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'If you need\nLegal Protection',
        description:
          'If you need a lawyer to defend you, we’ll help cover these costs with our Legal Protection Add-on.',
        img: {
          src: {
            lg: lawImgLg,
            sm: lawImgSm,
          },
          alt: '',
        },
      },
    ],
  },
  bottomCTA: {
    title: 'Prices that aren’t through the roof',
    subtitle: 'Homeowners insurance starting from 10 € /mo',
    windowImage,
    ctaUrl: '/onboarding/start?f=1&ownership=owner',
  },
  faq: {
    layout: {
      mt: [0, -22],
      pt: [60, 60],
    },
    darkBackground: true,
    subtitle:
      'Homeowners insurance can be confusing, so we worked hard to make it simple. If you still have questions, you can check out homeowners insurance explained.',
    faqInfo,
    showDisclaimer: false,
  },
  switchingEurope: {
    title: 'Already insured?\nSwitch to Lemonade instantly',
    ctaLabel: 'Check prices and switch',
    ctaUrl: '/onboarding/start?f=1&ownership=owner',
    leftLottieConfig: chatEnLottieConfig,
    centerLottieConfig: mapLottieConfig,
    rightLottieConfig: successLottieConfig,
    // desktop
    leftDesktopLottieConfig: chatEnDesktopLottieConfig,
    centerDesktopLottieConfig: mapDesktopLottieConfig,
    rightDesktopLottieConfig: successDesktopLottieConfig,
    steps: ['Get your\nLemonade policy', 'Give us your old\ndetails', 'We’ll handle all\nthe paperwork'],
  },
  products: {
    title: 'More products from Lemonade',
    darkBackground: true,
    products: [
      {
        title: 'Renters',
        subtitle: 'Coverage for your stuff inside and out of your home',
        price: 'FROM 4 €/MO',
        product: 'home_eu_renters',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'renters insurance',
      },
      {
        title: 'Landlords',
        subtitle: 'Coverage for your home while you’re renting it to a tenant',
        price: 'FROM 6 €/MO',
        product: 'landlords_eu',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=owner',
        alt: 'landlords insurance',
      },
    ],
  },
};
